import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useZendeskData } from "../../hooks";
import {
  zafClient,
  ZENDESK_FIELD_IDS,
  popupSuccess,
  popupError,
  getIncidentsBySideConversation,
} from "../../services/Zendesk";

interface ProductData {
  id: string;
  name: string;
  codigo: string;
  marca: string;
  linha: string;
  peso: string;
  altura: string;
  largura: string;
  profundidade: string;
  ativo: boolean;
  produtoComercial: string;
  familiaProduto: string;
  codigoErp: string;
  imageUrl?: string;
}

interface ProductResult {
  kind: "product";
  id: string;
  name: string;
  custom_object_key: "produto";
  custom_object_fields: Record<string, unknown>;
}

interface CustomObjectRecord {
  id: string;
  name: string;
  custom_object_key: string;
  custom_object_fields: Record<string, unknown>;
}

const uniqById = <T extends { id: string }>(arr: T[]) =>
  Array.from(new Map(arr.map((u) => [u.id, u])).values());

async function fetchProdutoRecordsAll(objectKey: string) {
  const url = `/api/v2/custom_objects/${objectKey}/records`;
  const res = (await zafClient.request({
    url,
    type: "GET",
  })) as { custom_object_records?: CustomObjectRecord[] };
  return res?.custom_object_records ?? [];
}

const normalizeProduct = (r: CustomObjectRecord): ProductResult => ({
  kind: "product",
  id: r.id,
  name:
    r.name ||
    (r.custom_object_fields?.produto_comercial as string) ||
    (r.custom_object_fields?.nome as string) ||
    (r.custom_object_fields?.descricao_do_produto as string) ||
    "(sem nome)",
  custom_object_key: "produto",
  custom_object_fields: r.custom_object_fields,
});

const defaultProductSearchFn = async (
  term: string,
  objectTypeKey = "produto"
): Promise<ProductResult[]> => {
  const t = term?.trim();
  if (!t) return [];

  const all = await fetchProdutoRecordsAll(objectTypeKey);
  
  // Log temporário para debug
  const targetProduct = all.find(r => 
    r.name?.includes("TV PTV32E20DSGWA") || 
    String(r.custom_object_fields?.codigo_do_produto).includes("99323048")
  );
  if (targetProduct) {
    console.log("🔍 DEBUG: Produto alvo encontrado:", {
      id: targetProduct.id,
      name: targetProduct.name,
      custom_object_fields: targetProduct.custom_object_fields
    });
  }

  const exact = all.filter(
    (r) => {
      const codigo = String(r.custom_object_fields?.codigo_do_produto ?? "").trim();
      const codigoNormalized = codigo.toLowerCase().replace(/\s+/g, "");
      const termNormalized = t.toLowerCase().replace(/\s+/g, "");
      
      // Log para debug do código específico
      if (codigo.includes("99323048") || termNormalized.includes("99323048")) {
        console.log("🔍 DEBUG: Código encontrado:", {
          codigo: codigo,
          codigoNormalized: codigoNormalized,
          termNormalized: termNormalized,
          matches: codigoNormalized === termNormalized
        });
      }
      
      return codigoNormalized === termNormalized;
    }
  );
  if (exact.length) {
    return uniqById(exact.map(normalizeProduct));
  }

  const lc = t.toLowerCase();
  const searchTerms = lc.split(/\s+/).filter(term => term.length > 0);
  
  const filtered = all.filter((r) => {
    const f = r.custom_object_fields || ({} as Record<string, unknown>);
    
    // Lista expandida de campos para busca - com normalização especial para códigos
    const searchableFields = [
      r.name,
      f.produto_comercial,
      f.descricao_do_produto,
      String(f.codigo_do_produto || ""),
      f.referencia_telecontrol,
      f.modelo,
      f.familia_do_produto,
      f.familia_produto,
      f.marca,
      f.nome,
      f.descricao,
      String(f.codigo || ""),
      f.tipo_de_registro,
      f.linha,
      f.produto,
      f.sku_do_produto,
      f.name,
      f.title,
      // Campos específicos que vi na imagem
      String(f.codigo_erp || ""),
      f.valor_base,
      String(f.peso || ""),
      String(f.altura || ""),
      String(f.largura || ""),
      String(f.profundidade || ""),
      f.origem,
      f.engenharia,
      f.lamina,
      // Tratamento especial para números que podem estar como string
      ...(Object.entries(f)
        .filter(([key, val]) => 
          (typeof val === 'string' || typeof val === 'number') &&
          String(val).length > 0 && 
          String(val).length < 200 &&
          !key.includes('http') && // Ignorar URLs
          !key.includes('image') && // Ignorar campos de imagem
          !key.includes('data') && // Ignorar datas
          !key.includes('inicio') // Ignorar datas de início
        )
        .map(([, val]) => String(val)))
    ];
    
    const searchText = searchableFields
      .filter(Boolean)
      .map(field => String(field).trim())
      .join(" ")
      .toLowerCase()
      .replace(/[^\w\s]/g, " ") // Remove caracteres especiais
      .replace(/\s+/g, " ") // Normaliza espaços
      .trim();
    
    // Log para o produto específico que queremos encontrar
    if (searchText.includes("tv ptv32e20dsgwa") || searchText.includes("99323048")) {
      console.log("🔍 DEBUG: Produto específico analisado:", {
        name: r.name,
        codigo_produto: f.codigo_do_produto,
        searchText: searchText,
        searchTerms: searchTerms,
        matches: searchTerms.map(term => ({ term, found: searchText.includes(term) }))
      });
    }
    
    // Busca específica para códigos numéricos
    if (/^\d+$/.test(lc)) {
      const codigoMatch = searchableFields.some(field => 
        String(field || "").replace(/\D/g, "").includes(lc)
      );
      if (codigoMatch) {
        console.log("🔍 DEBUG: Match por código numérico encontrado!");
        return true;
      }
    }
    
    // Busca por todos os termos - deve conter TODOS os termos digitados
    if (searchTerms.length > 1) {
      return searchTerms.every(term => searchText.includes(term));
    }
    
    // Busca simples para um único termo
    return searchText.includes(lc);
  });

  return uniqById(filtered.map(normalizeProduct));
};

function useDebouncedCallback(
  fn: (term: string) => Promise<void>,
  delay: number
) {
  const timer = useRef<number | null>(null);
  const cb = useCallback(
    (term: string) => {
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => fn(term), delay);
    },
    [fn, delay]
  );
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    []
  );
  return cb;
}

export function useNewProduct() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sideConversationId = searchParams.get("sideConversationId");

  const [step, setStep] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<ProductData | null>(
    null
  );
  const [quantidade, setQuantidade] = useState("");
  const [preco, setPreco] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { ticketId, requester } = useZendeskData();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ProductResult[]>([]);
  const [open, setOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [highlight, setHighlight] = useState<number>(-1);
  const [error, setError] = useState<string | null>(null);

  // Debug: monitorar mudanças no selectedProduct
  useEffect(() => {
    console.log("🔧 DEBUG: selectedProduct state changed:", selectedProduct);
    if (selectedProduct) {
      console.log("✅ DEBUG: Produto selecionado:", selectedProduct.name);
    }
  }, [selectedProduct]);

  const doSearch = useCallback(async (term: string) => {
    if (term.trim().length < 2) {
      setResults([]);
      setOpen(false);
      setSearchLoading(false);
      setError(null);
      return;
    }
    setSearchLoading(true);
    setError(null);
    try {
      const list = await defaultProductSearchFn(term.trim());
      setResults(list);
      setOpen(true);
      setHighlight(list.length ? 0 : -1);
    } catch (err) {
      const error = err as {
        response?: { data?: { error?: string } };
        message?: string;
      };
      const msg =
        error?.response?.data?.error || error?.message || "Falha na busca";
      setError(msg);
      setResults([]);
      setOpen(true);
      setHighlight(-1);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const debouncedSearch = useDebouncedCallback(doSearch, 300);

  const handleSearchChange = (value: string) => {
    setQuery(value);
    debouncedSearch(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && highlight < results.length) {
        const picked = results[highlight];
        handleProductSelect(picked);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleBlur = () => setTimeout(() => setOpen(false), 200); // Aumentar delay para 200ms

  const handleProductSelect = (productResult: ProductResult) => {
    console.log("🔧 DEBUG: handleProductSelect chamado com:", productResult);
    console.log("🔧 DEBUG: Product ID:", productResult.id);
    console.log("🔧 DEBUG: Product Name:", productResult.name);
    
    // Limpar estado visual primeiro
    setQuery("");
    setOpen(false);
    console.log("✅ DEBUG: Estado limpo antes da seleção");
    
    const productData: ProductData = {
      id: productResult.id,
      name: productResult.name,
      codigo: String(
        productResult.custom_object_fields?.codigo_do_produto || ""
      ),
      marca: String(productResult.custom_object_fields?.marca || ""),
      linha: String(productResult.custom_object_fields?.linha || ""),
      peso: productResult.custom_object_fields?.peso
        ? `${productResult.custom_object_fields.peso} g`
        : "",
      altura: productResult.custom_object_fields?.altura
        ? `${productResult.custom_object_fields.altura} mm`
        : "",
      largura: productResult.custom_object_fields?.largura
        ? `${productResult.custom_object_fields.largura} mm`
        : "",
      profundidade: productResult.custom_object_fields?.profundidade
        ? `${productResult.custom_object_fields.profundidade} mm`
        : "",
      ativo: Boolean(productResult.custom_object_fields?.ativo || false),
      produtoComercial: String(
        productResult.custom_object_fields?.produto_comercial || ""
      ),
      familiaProduto: String(
        productResult.custom_object_fields?.familia_do_produto ||
          productResult.custom_object_fields?.familia_produto ||
          ""
      ),
      codigoErp: String(productResult.custom_object_fields?.codigo_erp || ""),
      imageUrl: String(
        productResult.custom_object_fields?.image_url ||
          productResult.custom_object_fields?.imagem ||
          ""
      ),
    };

    console.log("✅ DEBUG: ProductData criado:", productData);
    setSelectedProduct(productData);
    console.log("✅ DEBUG: selectedProduct state atualizado");
  };

  const clearQuery = () => {
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const addProductToSideConversation = async () => {
    if (
      !selectedProduct ||
      !sideConversationId ||
      !ticketId ||
      !quantidade
    ) {
      popupError("Dados incompletos para adicionar o produto", false);
      return;
    }

    try {
      setIsLoading(true);

      const produtoId = selectedProduct.id;

      // Buscar incidentes existentes relacionados à side conversation
      console.log("🔍 Buscando incidentes existentes...");
      const existingIncidents = await getIncidentsBySideConversation(
        parseInt(ticketId.toString()),
        sideConversationId
      );

      // Buscar o ID do child ticket da side conversation
      const sideConversationResponse = await zafClient.request({
        url: `/api/v2/tickets/${ticketId}/side_conversations/${sideConversationId}.json`,
        type: "GET",
      });
      
      const childTicketId = (sideConversationResponse as { side_conversation: { external_ids: { targetTicketId: string } } })?.side_conversation?.external_ids?.targetTicketId;
      
      if (!childTicketId) {
        throw new Error("Child ticket ID não encontrado para esta side conversation");
      }
      
      console.log(`✅ Child ticket encontrado: #${childTicketId}`);

      const ticketResponse = await zafClient.request({
        url: `/api/v2/tickets/${ticketId}.json`,
        type: "GET",
      });

      const ticket = (ticketResponse as { ticket: { brand_id: number } }).ticket;
      const brandId = ticket.brand_id;

      // Debug do solicitante
      console.log(`🔧 DEBUG: Solicitante do NewProduct - requester ID: ${requester?.id}, nome: ${requester?.name}`);
      if (!requester?.id) {
        console.warn("⚠️ AVISO: Requester ID não encontrado, o ticket será criado sem solicitante específico");
      }

      const customFields: { id: number; value: string | number }[] = [
        {
          id: ZENDESK_FIELD_IDS.tipo,
          value: "incidente",
        },
        {
          id: ZENDESK_FIELD_IDS.quantidade,
          value: parseInt(quantidade),
        },
        {
          id: ZENDESK_FIELD_IDS.produtoObjeto,
          value: produtoId,
        },
      ];

      // Adicionar campo de preço apenas se tiver valor válido (regex: ^\d{1,3}(\.\d{3})*(,\d{2})?$)
      if (preco) {
        // Função para formatar preço no padrão brasileiro aceito pelo Zendesk
        const formatarPrecoParaZendesk = (input: string): string => {
          // Remover ruídos (R$, espaços)
          const limpo = input
            .replace(/\s/g, "")
            .replace(/R\$/g, "")
            .replace(/[^\d,.]/g, ""); // Remove tudo exceto dígitos, vírgula e ponto

          let valorNumerico: number;

          // Se tem vírgula como decimal (formato BR: 1.234,56)
          if (limpo.includes(",")) {
            const partes = limpo.split(",");
            if (partes.length === 2) {
              // Remove pontos da parte inteira e reconstrói
              const parteInteira = partes[0].replace(/\./g, "");
              const parteDecimal = partes[1];
              valorNumerico = parseFloat(`${parteInteira}.${parteDecimal}`);
            } else {
              // Só vírgula sem ponto, tratar como decimal
              valorNumerico = parseFloat(limpo.replace(",", "."));
            }
          } else {
            // Sem vírgula, remover todos os pontos e tratar como número inteiro ou decimal com ponto
            if (limpo.split(".").length > 2) {
              // Múltiplos pontos, considerar o último como decimal
              const partes = limpo.split(".");
              const parteDecimal = partes.pop();
              const parteInteira = partes.join("");
              valorNumerico = parseFloat(`${parteInteira}.${parteDecimal}`);
            } else {
              valorNumerico = parseFloat(limpo);
            }
          }

          if (isNaN(valorNumerico)) return "";

          // Formatar no padrão brasileiro: 1.234,56
          return valorNumerico.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        };

        const finalValue = formatarPrecoParaZendesk(preco);
        if (finalValue) {
          console.log(`🔧 DEBUG: Preço formatado para Zendesk (NewProduct - regex ^\\d{1,3}(\\.\\d{3})*(,\\d{2})?$): "${finalValue}" (original: "${preco}")`);
          customFields.push({
            id: ZENDESK_FIELD_IDS.preco,
            value: finalValue,
          });
        }
      }

      const incidentData = {
        ticket: {
          type: "incident",
          subject: `${selectedProduct.name}`,
          priority: "normal",
          ticket_form_id: 42958690377492,
          brand_id: brandId,
          problem_id: Number(childTicketId), // 👈 Liga o incident ao problema (child ticket)
          external_id: `side_conversation:${sideConversationId}`,
          comment: {
            body: `**Produto adicionado ao pedido:**\n\n• ${selectedProduct.name} - Qtd: ${quantidade}${preco ? ` - Valor: ${preco}` : ''}\n\n---\n*Ticket Principal: #${ticketId}*`,
            public: false,
          },
          custom_fields: customFields,
          requester_id: requester?.id, // Definir o solicitante correto baseado no ticket principal
        },
      };

      // Criar o novo incidente
      console.log("🚀 Criando novo incidente...");
      const newIncidentResponse = await zafClient.request({
        url: "/api/v2/tickets.json",
        type: "POST",
        contentType: "application/json",
        data: JSON.stringify(incidentData),
      });

      const newIncident = (newIncidentResponse as { ticket: { id: number } }).ticket;
      const newIncidentId = newIncident.id;

      // Preparar lista de todos os incidentes (existentes + novo)
      const existingIncidentIds = (existingIncidents as Array<{ id: number }>).map(incident => incident.id);
      const allIncidentIds = [...existingIncidentIds, newIncidentId];
      
      console.log(`✅ Novo incidente criado: #${newIncidentId}`);
      console.log(`📋 Total de incidentes: ${allIncidentIds.length} (#${allIncidentIds.join(", #")})`);

      // Calcular valor total considerando todos os produtos
      const totalProducts = existingIncidents.length + 1;
      
      // Buscar o ticket da side conversation para atualizar o comentário
      try {
        const sideConversationResponse = await zafClient.request({
          url: `/api/v2/tickets.json?external_id=side_conversation:${sideConversationId}`,
          type: "GET",
        });

        const sideConversationTickets = (sideConversationResponse as { tickets: Array<{ id: number }> }).tickets;
        
        if (sideConversationTickets.length > 0) {
          const sideConversationTicketId = sideConversationTickets[0].id;
          
          // Adicionar comentário atualizado na side conversation
          await zafClient.request({
            url: `/api/v2/tickets/${sideConversationTicketId}.json`,
            type: "PUT",
            contentType: "application/json",
            data: JSON.stringify({
              ticket: {
                comment: {
                  body: `🔗 **Incidents Relacionados Criados**\n\nIncidents #${allIncidentIds.join(", #")} foram criados para este pedido.\n\n📋 **Detalhes:**\n• Formulário: 42958690377492 (Itens do pedido)\n• Produtos: ${totalProducts} item(s)\n• Incidents criados: ${allIncidentIds.length}\n• Último produto adicionado: ${selectedProduct.name} (Qtd: ${quantidade}${preco ? ` - Valor: ${preco}` : ''})\n• Data: ${new Date().toLocaleString("pt-BR")}\n\n---\n*Comentário automático do sistema*`,
                  public: false,
                  type: "Comment",
                },
              },
            }),
          });
          
          console.log(`✅ Comentário atualizado na side conversation #${sideConversationTicketId}`);
        }
      } catch (error) {
        console.warn("⚠️ Não foi possível atualizar o comentário na side conversation:", error);
      }

      setStep(3); // Ir para a tela de sucesso
      popupSuccess(`Produto adicionado com sucesso! Incidente #${newIncidentId} criado.`);
    } catch (error) {
      console.error("❌ Erro ao adicionar produto:", error);
      popupError("Erro ao adicionar produto ao pedido", false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToOrder = () => {
    navigate("/");
  };

  return {
    navigate,
    step,
    setStep,
    sideConversationId,
    isLoading,
    selectedProduct,
    setSelectedProduct,
    quantidade,
    setQuantidade,
    preco,
    setPreco,
    addProductToSideConversation,
    handleBackToOrder,
    query,
    results,
    open,
    searchLoading,
    error,
    highlight,
    handleSearchChange,
    handleKeyDown,
    handleBlur,
    clearQuery,
    handleProductSelect,
  };
}
